// Mirrors the public surface of window.tractive (engvis/tractive, tractivejs/tractive.d.ts)
// so callers that skip a guard keep working when tracking is blocked for a brand.
export interface TractiveApi {
  configure: (options?: Record<string, unknown>) => TractiveApi;
  track: (
    billingTeam: string,
    eventName: string,
    customAttributes?: Record<string, unknown>,
  ) => void;
  identify: (userProperties: Record<string, unknown>) => void;
  groupIdentify: (
    groupType: string,
    groupName: string,
    groupProperties?: Record<string, unknown>,
  ) => void;
  start: () => void;
  stop: () => void;
  reset: () => void;
  readonly isRunning: boolean;
  readonly loggedEvents: string[];
  readonly previousEventId: string | null;
  readonly previousEventNumber: number | null;
  readonly registeredEvents: ReadonlyArray<unknown>;
  readonly identifiers: Record<string, unknown>;
  readonly configurations: Record<string, unknown>;
}

// @qualtrics/page-metrics already declares a narrower global Window.tractive; a second global
// declaration would clash with it, so this file types the property through a local cast.
export const tractiveWindow = (): { tractive?: Partial<TractiveApi> } =>
  window as unknown as { tractive?: Partial<TractiveApi> };

// onAmplitudeCall runs when a page calls a method that would have sent data to Amplitude.
export const installTractiveNoopStub = (
  onAmplitudeCall: () => void = () => {},
): void => {
  // The real script may already be running (an app injected it before us). Remove its DOM
  // listeners first, then replace the object so later track/identify calls hit the no-ops.
  // Events already sent, Amplitude's own SDK state and the client_config poll are not undone.
  const existing = tractiveWindow().tractive;
  if (typeof existing?.stop === 'function') {
    try {
      existing.stop();
    } catch (error) {
      console.error(
        'Global Wrapper failed to stop the running tractive:',
        error,
      );
    }
  }

  const noop = (): void => {};
  const stub: TractiveApi = {
    configure: () => stub,
    track: onAmplitudeCall,
    identify: onAmplitudeCall,
    groupIdentify: onAmplitudeCall,
    start: noop,
    stop: noop,
    reset: noop,
    // true so sendTractiveMetric calls track() right away instead of polling for ~9s
    isRunning: true,
    loggedEvents: [],
    previousEventId: null,
    previousEventNumber: null,
    registeredEvents: [],
    identifiers: {},
    configurations: {},
  };

  tractiveWindow().tractive = stub;
};
